import { Link, useLocation } from "react-router";
import classNames from "classnames";
import { Button, Icon, Icons, Span } from "@ir/ui";
import { type NavigationHookReturn } from "./hooks";
import { type NavigationItemProps } from "./types";

interface SlideNavItemProps extends NavigationHookReturn {
  item: NavigationItemProps;
  title?: string;
}

const NavigationItem: React.FC<SlideNavItemProps> = ({
  item,
  title = item.label,
  isItemActive,
  isItemOpen,
  isCurrentLinkActive,
  toggleItem,
  handleItemClick,
  openItems,
}) => {
  const location = useLocation();
  const _isActiveItem = isItemActive(item);
  const _isActiveItemOpen = isItemOpen(item.key);
  const _isCurrentLinkActive = isCurrentLinkActive(item);

  const navigationClassNames = classNames("slide-navigation-item", {
    active: _isActiveItem,
  });

  const mainClassNames = classNames("slide-navigation-main", {
    "has-children": item.children,
  });

  const linkClassNames = classNames(
    navigationClassNames,
    "button button-clear button-primary button-medium",
    {
      active: _isCurrentLinkActive,
    },
  );

  const renderWrapper = () => {
    const menuItemRender = () => (
      <>
        {item.icon && (
          <Span className="slide-navigation-icon" title={title}>
            {item.icon}
          </Span>
        )}
        {item.label && (
          <Span className="slide-navigation-label">{item.label}</Span>
        )}
        {item.suffix && (
          <Span className="slide-navigation-suffix">{item.suffix}</Span>
        )}
        {showArrow() && (
          <Span className="slide-navigation-expand">{showArrow()}</Span>
        )}
      </>
    );

    const showArrow = () => {
      if (item.children) {
        return (
          <>
            {_isActiveItem || _isActiveItemOpen ? (
              <Icon name={Icons.ChevronDown} />
            ) : (
              <Icon name={Icons.ChevronRight} />
            )}
          </>
        );
      }
    };

    if (_isCurrentLinkActive || !item.link) {
      return (
        <Button
          variant="clear"
          className={navigationClassNames}
          onPress={() => toggleItem(item.key)}
        >
          {menuItemRender()}
        </Button>
      );
    }

    return (
      <Link
        to={item.link}
        state={location.state}
        className={linkClassNames}
        onClick={() => toggleItem(item.key)}
      >
        <Span className="button-children">{menuItemRender()}</Span>
      </Link>
    );
  };

  return (
    <li className={mainClassNames}>
      {renderWrapper()}
      {(_isActiveItem || _isActiveItemOpen) && item.children && (
        <ul className="submenu">
          {item.children.map((child) => (
            <NavigationItem
              key={child.key}
              item={child}
              isItemActive={isItemActive}
              isCurrentLinkActive={isCurrentLinkActive}
              toggleItem={toggleItem}
              isItemOpen={isItemOpen}
              handleItemClick={handleItemClick}
              openItems={openItems}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

export default NavigationItem;
